// Portfolio detail view (carousel, specs, review)
import { escapeAttr, escapeHtmlText, formatRub, presetSpecsPairs } from '../../core/format.js';
import { openPhotoModalHero } from '../../app/photo-modal.js';
import { showView } from '../../app/navigation.js';
import { attachPresetImageCarousel, type PresetCarouselApi } from '../presets/detail.js';
import type { PortfolioItem } from '../../types/api.js';

function portfolioKindLabel(kind: string): string {
  if (kind === 'catalog') return 'Готовая';
  if (kind === 'upgrade') return 'Апгрейд';
  return 'На заказ';
}

function portfolioStarsHtml(rating: number | string | undefined, max = 5): string {
  const n = Math.max(0, Math.min(max, parseInt(String(rating), 10) || 0));
  let out = '';
  for (let i = 1; i <= max; i++) out += i <= n ? '★' : '☆';
  return out;
}

function formatPortfolioBudget(rub: string | number | undefined): string {
  const n = Math.round(Number(rub));
  if (!Number.isFinite(n) || n <= 0) return '';
  return 'Бюджет ~' + formatRub(n);
}

export function showPortfolioDetail(item: PortfolioItem): void {
  const wrap = document.getElementById('portfolio-detail-image-wrap');
  const titleEl = document.getElementById('portfolio-detail-title');
  const descEl = document.getElementById('portfolio-detail-desc');
  if (!wrap || !titleEl || !descEl) return;
  const imgs: string[] = [];
  if (Array.isArray(item.images) && item.images.length) imgs.push(...item.images.filter(Boolean) as string[]);
  if (!imgs.length && item.image) imgs.push(item.image);

  if (imgs.length) {
    let track = '<div class="preset-detail-carousel" id="portfolio-detail-carousel">';
    track += '<div class="preset-detail-carousel-track" id="portfolio-detail-carousel-track">';
    for (let i = 0; i < imgs.length; i++) {
      track += '<div class="preset-detail-carousel-slide"><img src="' + escapeAttr(imgs[i]) + '" alt="" /></div>';
    }
    track += '</div>';
    if (imgs.length > 1) {
      track += '<div class="preset-detail-dots" id="portfolio-detail-dots">';
      for (let d = 0; d < imgs.length; d++) {
        track += '<span class="preset-detail-dot' + (d === 0 ? ' preset-detail-dot-active' : '') + '"></span>';
      }
      track += '</div>';
    }
    track += '</div>';
    wrap.innerHTML = track;
    wrap.classList.remove('hidden');

    const carouselEl = document.getElementById('portfolio-detail-carousel');
    const trackEl = document.getElementById('portfolio-detail-carousel-track');
    const dotsWrapEl = document.getElementById('portfolio-detail-dots');
    const slideImgs = wrap.querySelectorAll('.preset-detail-carousel-slide img');

    let carouselApi: PresetCarouselApi | null = null;
    const openPortfolioPhoto = (galleryIndex: number, ev: PointerEvent): void => {
      try {
        const heroImg = (slideImgs[galleryIndex] as HTMLImageElement | undefined) ||
          (slideImgs[0] as HTMLImageElement);
        openPhotoModalHero(heroImg, ev, {
          galleryUrls: imgs.length > 1 ? imgs : undefined,
          galleryIndex,
          onGalleryIndexChange: (i) => carouselApi?.goTo(i, false)
        });
      } catch (_) {}
    };

    carouselApi = attachPresetImageCarousel(
      carouselEl,
      trackEl,
      imgs.length,
      dotsWrapEl,
      imgs.length > 1 ? openPortfolioPhoto : undefined
    );

    wrap.querySelectorAll('img').forEach(function (imgEl) {
      (imgEl as HTMLImageElement).style.cursor = 'pointer';
      if (imgs.length > 1) return;
      imgEl.addEventListener('click', function (ev) {
        try {
          openPhotoModalHero(imgEl as HTMLImageElement, ev);
        } catch (_) {}
      });
    });
  } else {
    wrap.innerHTML = '';
    wrap.classList.add('hidden');
  }

  titleEl.textContent = item.title || '';
  descEl.textContent = item.description || '';

  const metaEl = document.getElementById('portfolio-detail-meta');
  if (metaEl) {
    const kind = String(item.kind || 'custom');
    let dateStr = '';
    if (item.createdAt) {
      try {
        dateStr = new Date(item.createdAt).toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
      } catch (_) {}
    }
    metaEl.innerHTML =
      '<span class="portfolio-detail-kind">' + escapeHtmlText(portfolioKindLabel(kind)) + '</span>' +
      (dateStr ? '<span class="portfolio-detail-date">' + escapeHtmlText(dateStr) + '</span>' : '');
  }

  const budgetEl = document.getElementById('portfolio-detail-budget');
  if (budgetEl) {
    const budgetText = formatPortfolioBudget(item.budgetRub);
    if (budgetText) {
      budgetEl.hidden = false;
      budgetEl.textContent = budgetText;
    } else {
      budgetEl.hidden = true;
      budgetEl.textContent = '';
    }
  }

  const specsWrap = document.getElementById('portfolio-detail-specs-wrap');
  const specsEl = document.getElementById('portfolio-detail-specs');
  if (specsWrap && specsEl) {
    const pairs = presetSpecsPairs(item.specs || item);
    const hasSpecs = pairs.some(function (p) {
      return p[1];
    });
    if (hasSpecs) {
      specsWrap.hidden = false;
      specsEl.innerHTML = pairs
        .filter(function (p) {
          return p[1];
        })
        .map(function (p) {
          return (
            '<div class="preset-spec-row"><span class="preset-spec-label">' +
            escapeHtmlText(p[0]) +
            '</span><span class="preset-spec-value">' +
            escapeHtmlText(p[1]) +
            '</span></div>'
          );
        })
        .join('');
    } else {
      specsWrap.hidden = true;
      specsEl.innerHTML = '';
    }
  }

  const reviewWrap = document.getElementById('portfolio-detail-review-wrap');
  const reviewText = document.getElementById('portfolio-detail-review-text');
  const reviewAuthor = document.getElementById('portfolio-detail-review-author');
  const reviewStars = document.getElementById('portfolio-detail-review-stars');
  const review = item.review && item.review.text ? item.review : null;
  if (reviewWrap) {
    if (review) {
      reviewWrap.hidden = false;
      if (reviewText) reviewText.textContent = review.text || '';
      if (reviewStars) reviewStars.textContent = portfolioStarsHtml(review.rating, 5);
      if (reviewAuthor) {
        const src = review.source === 'avito' ? ' · Авито' : '';
        reviewAuthor.textContent = (review.author || 'Покупатель') + src;
      }
    } else {
      reviewWrap.hidden = true;
    }
  }

  showView('view-portfolio-detail');
}
